import type { Socket } from 'socket.io-client';
import type { GameAction, PublicGameState } from '@durakapp/engine';
import type { MatchEndReason, Stake, TableSize } from '@durakapp/shared';
import { getSocket, currentSocket } from './socket.js';
import { t, translateError } from './i18n.js';
import { useAuth } from '../stores/auth.js';
import { useLobby } from '../stores/lobby.js';
import { useMatch } from '../stores/match.js';
import { useToast } from '../stores/toast.js';
import { useReactions } from '../stores/reactions.js';
import { sfx } from './sound.js';

export interface ServerLobbyQueued {
  type: 'lobby:queued';
  stake: Stake;
  tableSize: TableSize;
}
export interface ServerLobbyLeft { type: 'lobby:left_queue' }
export interface ServerMatchStart {
  type: 'match:start';
  matchId: string;
  state: PublicGameState;
  turnDeadlineAt?: number;
}
export interface ServerMatchState {
  type: 'match:state';
  matchId: string;
  state: PublicGameState;
  turnDeadlineAt?: number;
}
export interface ServerMatchEnd {
  type: 'match:end';
  matchId: string;
  state: PublicGameState;
  endReason?: MatchEndReason;
  forfeitedBy?: string;
}
export interface ServerMatchError {
  type: 'match:error';
  actionId?: string;
  code: string;
  message: string;
}
export interface ServerMatchReaction {
  type: 'match:reaction';
  matchId: string;
  fromUserId: string;
  emoji: string;
  ts: number;
}

let onMatchStart: ((m: ServerMatchStart) => void) | null = null;

/** Caller (App / router) registers what to do on `match:start` (e.g. navigate). */
export function setMatchStartHandler(fn: (m: ServerMatchStart) => void): void {
  onMatchStart = fn;
}

export function attachGameListeners(token: string): Socket {
  const socket = getSocket(token);

  socket.on('connect', () => {
    useToast.getState();
  });
  socket.on('connect_error', (err: Error) => {
    useToast.getState().push(`Соединение: ${err.message}`, 'error');
  });
  socket.on('disconnect', () => {
    useToast.getState().push(t.connectionLost, 'info');
  });

  socket.on('lobby:queued', (msg: ServerLobbyQueued) => {
    useLobby.getState().setStatus('queued');
    useLobby.getState().setError(null);
    useToast.getState().push(t.queued(msg.tableSize, msg.stake), 'info');
  });

  socket.on('lobby:left_queue', () => {
    useLobby.getState().setStatus('idle');
  });

  socket.on('match:start', (msg: ServerMatchStart) => {
    useMatch.getState().setMatch(msg.matchId, msg.state, msg.turnDeadlineAt ?? null);
    useLobby.getState().setStatus('matched');
    useLobby.getState().setMatchId(msg.matchId);
    sfx.matchStart();
    onMatchStart?.(msg);
  });

  socket.on('match:state', (msg: ServerMatchState) => {
    useMatch.getState().setState(msg.state, msg.turnDeadlineAt ?? null);
  });

  socket.on('match:end', (msg: ServerMatchEnd) => {
    useMatch.getState().markEnded(msg.state, msg.endReason ?? null, msg.forfeitedBy ?? null);
    // Play the right cue based on the viewer's outcome (state.loserId is set on the server).
    const myId = useAuth.getState().user?.id;
    const s = msg.state;
    if (s.isDraw) sfx.draw();
    else if (s.loserId === myId) sfx.lose();
    else if (s.loserId !== null) sfx.win();
    // Pull fresh stats — backend just bumped totalGames/totalWins/rating + balance
    void useAuth.getState().refresh();
  });

  socket.on('match:error', (msg: ServerMatchError) => {
    useToast.getState().push(translateError(msg.code, msg.message), 'error');
  });

  socket.on('match:reaction', (msg: ServerMatchReaction) => {
    useReactions.getState().push({
      fromUserId: msg.fromUserId,
      emoji: msg.emoji,
      ts: msg.ts,
    });
  });

  return socket;
}

export function detachGameListeners(): void {
  const socket = currentSocket();
  if (!socket) return;
  socket.removeAllListeners();
}

export interface JoinQueueArgs {
  stake: Stake;
  tableSize: TableSize;
}

export function emitJoinQueue(args: JoinQueueArgs): Promise<void> {
  const socket = currentSocket();
  if (!socket) return Promise.reject(new Error('socket not connected'));
  return new Promise<void>((resolve, reject) => {
    socket.emit('lobby:join_queue', args, (resp: ServerLobbyQueued | ServerMatchError) => {
      if (resp.type === 'match:error') {
        // Pass the synthetic INSUFFICIENT_BALANCE:N untouched so the lobby can parse it,
        // translate everything else.
        if (resp.code === 'BAD_REQUEST' && resp.message.startsWith('INSUFFICIENT_BALANCE:')) {
          reject(new Error(resp.message));
          return;
        }
        reject(new Error(translateError(resp.code, resp.message)));
        return;
      }
      resolve();
    });
  });
}

export function emitLeaveQueue(): void {
  currentSocket()?.emit('lobby:leave_queue');
}

export function emitReaction(matchId: string, emoji: string): void {
  currentSocket()?.emit('match:reaction', { matchId, emoji });
}

export interface EmitActionArgs {
  matchId: string;
  action: GameAction;
}

export function emitAction(args: EmitActionArgs): Promise<void> {
  const socket = currentSocket();
  if (!socket) return Promise.reject(new Error('socket not connected'));
  const actionId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  return new Promise<void>((resolve, reject) => {
    socket.emit(
      'match:action',
      { matchId: args.matchId, actionId, action: args.action },
      (resp: ServerMatchState | ServerMatchEnd | ServerMatchError) => {
        if (resp.type === 'match:error') {
          reject(new Error(translateError(resp.code, resp.message)));
          return;
        }
        resolve();
      },
    );
  });
}
